import React, { useState } from 'react';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import UserApi from '../../api/platform/userApi';
import { useSystemError } from '../../components/Error/SystemErrorContext';
import styles from '../../css/setting/settings-content.module.css';
import { ResponseErrorModel } from '../../models/system/responseErrorModel';
import {
  handleError,
  mapResponseErrorToSystemError,
  mapUnknownErrorToSystemError
} from '../../utils/errorUtils';

const PasswordSetting: React.FC = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [didErrorOccur, setDidErrorOccur] = useState(false);
  const { triggerSystemError } = useSystemError();

  const validatePassword = () => {
    if (!currentPassword || !newPassword) {
      setPasswordError('All fields are required.');
      return false;
    }
    return true;
  };

  const fetchUpdatePassword = async (currentPassword: string, newPassword: string) => {
    try {
      const response = await UserApi.updatePassword(currentPassword, newPassword);
      if (response.success) {
        setSuccessMessage(response.message);
        setPasswordError(null);
      } else {
        handleError(
          mapResponseErrorToSystemError(response as ResponseErrorModel),
          setPasswordError,
          setDidErrorOccur,
          triggerSystemError
        );
      }
    } catch (error: unknown) {
      handleError(
        mapUnknownErrorToSystemError(error, false),
        setPasswordError,
        setDidErrorOccur,
        triggerSystemError
      );
    }
  };

  const handlePasswordUpdate = async () => {
    setPasswordError(null);
    setSuccessMessage(null);
    if (!validatePassword()) return;
    await fetchUpdatePassword(currentPassword, newPassword);
  };

  return (
    <div className={styles.settingsContainer}>
      <form className={styles.formLayout} onSubmit={(e) => { e.preventDefault(); handlePasswordUpdate(); }}>
        <div className={styles.formGroup}>
          <label htmlFor="currentPassword">Current Password</label>
          <div className={styles.passwordWrapper}>
            <input
              id="currentPassword"
              className={styles.passwordInput}
              type={showCurrentPassword ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
            <span
              className={styles.eyeIcon}
              onMouseDown={() => setShowCurrentPassword(true)}
              onMouseUp={() => setShowCurrentPassword(false)}
            >
              {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
            </span>
          </div>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="newPassword">New Password</label>
          <div className={styles.passwordWrapper}>
            <input
              id="newPassword"
              className={styles.passwordInput}
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <span
              className={styles.eyeIcon}
              onMouseDown={() => setShowNewPassword(true)}
              onMouseUp={() => setShowNewPassword(false)}
            >
              {showNewPassword ? <VisibilityOff /> : <Visibility />}
            </span>
          </div>
        </div>

        <button type="submit" className={styles.submitButton}>
          Update Password
        </button>

        {passwordError && <div className={styles.errorMessage}>{passwordError}</div>}
        {successMessage && <div className={styles.successMessage}>{successMessage}</div>}
      </form>
    </div>
  );
};

export default PasswordSetting;
